// One square photo tile in an album's grid.
//
// Extracted from `routes/AlbumDetail.tsx` under the 15 KB route ceiling
// (`AGENTS.md §3 #10`). The `onError` fallback is the interesting part: the
// database can hold `Media` rows whose files were removed from disk, and without
// it the user sees a broken image rather than the Camera placeholder. The album
// cards in `routes/Albums.tsx` do the same thing for their covers.
//
// W39.93. This tile used to carry a private `useLongPressSimulator` whose only
// trigger was `onContextMenu`, so the long press every document in this repo
// describes did not exist here: a real 700 ms press-and-hold measured at
// 393×852 fell through to the click and opened `photo-lightbox` instead of
// `album-photo-action-sheet`. The canonical hook now owns the gesture, and the
// right-click path stays because it is the one that already worked.

import { useState } from "react";
import { Camera } from "lucide-react";

import { useLongPress } from "@/lib/gestures/longPress";
import { mediaFileUrl } from "@/lib/api/album";
import type { AlbumItemRead } from "@/lib/api/album";

export interface PhotoTileProps {
  item: AlbumItemRead;
  index: number;
  onOpen: (index: number) => void;
  onLongPress: (itemId: string) => void;
}

export function PhotoTile({ item, index, onOpen, onLongPress }: PhotoTileProps) {
  const [broken, setBroken] = useState(false);
  const pressAndHold = useLongPress({
    thresholdMs: 250,
    onLongPress: () => onLongPress(item.id),
  });
  return (
    <button
      type="button"
      onClick={() => onOpen(index)}
      onPointerDown={pressAndHold}
      onContextMenu={(e) => {
        // Desktop's version of the same gesture. A right press never starts the
        // hook (it returns early for `button !== 0`), so this cannot double-fire
        // with a long press — and when a long press *has* fired, the hook has
        // already armed a one-shot `contextmenu` swallow for exactly this event.
        e.preventDefault();
        onLongPress(item.id);
      }}
      data-testid={`album-photo-${item.id}`}
      data-photo-id={item.id}
      data-media-id={item.media.id}
      aria-label={`第 ${index + 1} 張照片`}
      className="press aspect-square rounded-md overflow-hidden bg-paper-deep group relative"
    >
      {broken ? (
        <div className="absolute inset-0 flex items-center justify-center text-paper/70" aria-hidden>
          <Camera size={24} />
        </div>
      ) : (
        <img
          src={mediaFileUrl(item.media.id)}
          alt={item.caption ?? ""}
          className="w-full h-full object-cover transition-transform duration-quick ease-glass group-hover:scale-[1.04]"
          loading="lazy"
          onError={() => setBroken(true)}
        />
      )}
      {item.caption ? (
        <span
          className="absolute bottom-0 left-0 right-0 px-1.5 py-1 text-body-xs text-paper bg-gradient-to-t from-ink-1/80 to-transparent line-clamp-2"
          data-testid="album-photo-caption"
        >
          {item.caption}
        </span>
      ) : null}
    </button>
  );
}
